// src/admin/pages/UserMaintenance.jsx
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { adminService } from "../../services/adminService";
import { useUser } from "../../UserContext";

const ROLE_IDS = {
  USER: "fe42ee60-9133-4f0e-b543-740d8697c23b",
  ADMIN: "ac9be322-db89-4beb-854a-46f2c91de374",
  SUPER_ADMIN: "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef",
};

const UserMaintenance = () => {
  const { user } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const roleName =
    user?.role_name ||
    (user?.user_role === ROLE_IDS.ADMIN
      ? "Admin"
      : user?.user_role === ROLE_IDS.SUPER_ADMIN
        ? "Super Admin"
        : user?.user_role === ROLE_IDS.USER
          ? "User"
          : null);

  const normalizedRole = String(user?.user_role || "").toLowerCase();
  const isSuperAdmin =
    roleName === "Super Admin" || normalizedRole === "super_admin";

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }

    const loadUsers = async () => {
      try {
        const data = await adminService.getUsers();
        setUsers(data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [isSuperAdmin, user]);

  const normalizedUsers = useMemo(() => {
    return users
      .map((u) => {
        const id = u.user_id || u.userid || u.id;
        return { ...u, id };
      })
      .filter((u) => u.id);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalizedUsers;

    return normalizedUsers.filter((u) => {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return (
        name.toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q) ||
        String(u.id || "").toLowerCase().includes(q)
      );
    });
  }, [normalizedUsers, search]);

  const selectedUser = useMemo(() => {
    return normalizedUsers.find((u) => u.id === selectedUserId) || null;
  }, [normalizedUsers, selectedUserId]);

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsDeleting(true);

    try {
      await adminService.deleteUserTransactions(selectedUser.id);
      toast.success("User progress data cleared.");
      setConfirmText("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete user progress data.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-[#54B0AF]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-display">User Maintenance</h1>
      </div>

      {!isSuperAdmin && (
        <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6 text-red-600 text-title">
          Access restricted. Super Admin only.
        </div>
      )}

      {isSuperAdmin && (
        <div className="bg-white rounded-lg shadow stakeholder-summary-card p-6 space-y-6">
          <div>
            <h2 className="text-title font-semibold">
              Delete User Progress Data
            </h2>
            <p className="text-muted text-sm">
              This permanently clears progress data for the selected user.
            </p>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-title">
                Search user
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, or user ID"
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-title">
                Select user
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select a user</option>
                {filteredUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name || u.last_name
                      ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                      : u.email || "Unnamed user"}{" "}
                    - {u.email || "no email"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedUser && (
            <div className="rounded-md bg-[#F5F9F9] p-4 text-sm space-y-1">
              <div className="font-semibold text-title">Selected user</div>
              <div className="text-muted">
                <span className="font-medium">Name:</span>{" "}
                {`${selectedUser.first_name || ""} ${
                  selectedUser.last_name || ""
                }`.trim() || "Unnamed user"}
              </div>
              <div className="text-muted">
                <span className="font-medium">Email:</span>{" "}
                {selectedUser.email || "no email"}
              </div>
              <div className="text-muted">
                <span className="font-medium">User ID:</span>{" "}
                {selectedUser.id}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-title">
                Type DELETE to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>

            <button
              type="button"
              onClick={handleDelete}
              disabled={
                !selectedUser || confirmText !== "DELETE" || isDeleting
              }
              className="px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? "Deleting..." : "Delete progress data"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMaintenance;
